import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Calendar, Phone, Mail, CheckCircle2, AlertCircle, Loader2, RefreshCw, ExternalLink, Unplug, Copy, Check } from 'lucide-react';
import { auth } from '../../../firebase/firebase.config';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../../firebase/firebase.config';
import {
    getCalendlyStatus,
    connectCalendly,
    disconnectCalendly,
    syncCalendlyEvents,
} from '../../../firebase/meetings.service';
import useAppStore from '../../../store/appStore';

const IntegrationsSection = ({ isMobile }) => {
    const [searchParams, setSearchParams] = useSearchParams();

    // Zustand cache for integration statuses
    const settingsIntegrations = useAppStore((s) => s.settingsIntegrations);
    const setSettingsIntegrations = useAppStore((s) => s.setSettingsIntegrations);

    // Calendly OAuth state
    const [calendlyStatus, setCalendlyStatus] = useState({ connected: false });
    const [calendlyLoading, setCalendlyLoading] = useState(true);
    const [calendlyConnecting, setCalendlyConnecting] = useState(false);
    const [calendlyDisconnecting, setCalendlyDisconnecting] = useState(false);
    const [calendlySyncing, setCalendlySyncing] = useState(false);
    const [calendlyError, setCalendlyError] = useState('');
    const [calendlySuccess, setCalendlySuccess] = useState('');

    // Twilio / SendGrid integration state
    const [twilioSid, setTwilioSid] = useState('');
    const [twilioToken, setTwilioToken] = useState('');
    const [twilioPhone, setTwilioPhone] = useState('');
    const [sendgridApiKey, setSendgridApiKey] = useState('');
    const [sendgridFromEmail, setSendgridFromEmail] = useState('');
    const [inboundDomain, setInboundDomain] = useState('');
    const [emailInboundEnabled, setEmailInboundEnabled] = useState(false);
    const [twilioConnected, setTwilioConnected] = useState(false);
    const [emailConnected, setEmailConnected] = useState(false);
    const [twilioSaving, setTwilioSaving] = useState(false);
    const [copiedField, setCopiedField] = useState('');

    const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://getkaeros-agent-portal-backend.hf.space';
    const twilioWebhookUrl = `${BACKEND_BASE_URL}/api/inbound/twilio-sms`;
    const sendgridWebhookUrl = `${BACKEND_BASE_URL}/inbound/sendgrid`;

    // Load Calendly status
    const loadCalendlyStatus = useCallback(async () => {
        const userId = auth.currentUser?.uid;
        if (!userId) return;

        try {
            setCalendlyLoading(true);
            const status = await getCalendlyStatus(userId);
            setCalendlyStatus(status);
            return status;
        } catch (error) {
            console.error('Error loading Calendly status:', error);
            return null;
        } finally {
            setCalendlyLoading(false);
        }
    }, []);

    // Load Twilio / SendGrid integration
    useEffect(() => {
        const loadIntegrations = async () => {
            const userId = auth.currentUser?.uid;
            if (!userId) return;

            try {
                const twilioDoc = await getDoc(doc(db, 'users', userId, 'integrations', 'twilio'));
                if (twilioDoc.exists()) {
                    const data = twilioDoc.data();
                    setTwilioSid(data.accountSid || '');
                    setTwilioToken(data.authToken ? '••••••••' : '');
                    setTwilioPhone(data.phoneNumber || '');
                    setTwilioConnected(!!(data.accountSid && data.authToken && data.phoneNumber));
                    setSendgridApiKey(data.sendgridApiKey ? '••••••••' : '');
                    setSendgridFromEmail(data.sendgridFromEmail || '');
                    setInboundDomain(data.inboundDomain || '');
                    setEmailInboundEnabled(!!data.emailInboundEnabled);
                    setEmailConnected(!!(data.sendgridApiKey && data.sendgridFromEmail));
                }
            } catch (error) {
                console.error('Error loading integrations:', error);
            }
        };

        // If we have cached integration statuses, pre-populate connection badges
        if (settingsIntegrations) {
            if (settingsIntegrations.calendlyStatus) {
                setCalendlyStatus(settingsIntegrations.calendlyStatus);
            }
            if (settingsIntegrations.twilioConnected !== undefined) {
                setTwilioConnected(settingsIntegrations.twilioConnected);
            }
            if (settingsIntegrations.emailConnected !== undefined) {
                setEmailConnected(settingsIntegrations.emailConnected);
            }
            if (settingsIntegrations.emailInboundEnabled !== undefined) {
                setEmailInboundEnabled(settingsIntegrations.emailInboundEnabled);
            }
        }

        // Always fetch fresh data (integrations involve credentials/connection statuses)
        loadCalendlyStatus().then((status) => {
            if (status) {
                // Write-through calendly status to Zustand
                setSettingsIntegrations({
                    ...(settingsIntegrations || {}),
                    calendlyStatus: status,
                });
            }
        });
        loadIntegrations();
    }, [loadCalendlyStatus]); // eslint-disable-line

    // Handle OAuth redirect result from URL query params
    // Backend redirects to /settings?section=integrations&calendly=success|error|denied
    useEffect(() => {
        const calendlyResult = searchParams.get('calendly');
        if (!calendlyResult) return;

        // Clean the query params from the URL immediately
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('calendly');
        newParams.delete('message');
        setSearchParams(newParams, { replace: true });

        if (calendlyResult === 'success') {
            setCalendlySuccess('Calendly connected successfully!');
            loadCalendlyStatus();
        } else if (calendlyResult === 'denied') {
            setCalendlyError('Calendly authorization was denied');
        } else if (calendlyResult === 'error') {
            const message = searchParams.get('message') || 'Connection failed';
            setCalendlyError(message);
        }

        setCalendlyConnecting(false);

        // Clear messages after 5 seconds
        setTimeout(() => {
            setCalendlySuccess('');
            setCalendlyError('');
        }, 5000);
    }, [searchParams, setSearchParams, loadCalendlyStatus]);

    const handleConnectCalendly = async () => {
        setCalendlyConnecting(true);
        setCalendlyError('');
        setCalendlySuccess('');

        try {
            // This navigates the browser away — no return value
            await connectCalendly();
        } catch (err) {
            console.error('[calendly] Connect error:', err);
            setCalendlyError(err.message || 'Failed to start Calendly connection');
            setCalendlyConnecting(false);
        }
    };

    const handleDisconnectCalendly = async () => {
        if (!window.confirm('Disconnect Calendly? Your synced meetings will be removed.')) return;

        setCalendlyDisconnecting(true);
        setCalendlyError('');
        const success = await disconnectCalendly();
        if (success) {
            setCalendlyStatus({ connected: false });
            setCalendlySuccess('Calendly disconnected');
            // Write-through: update Zustand cache
            setSettingsIntegrations({
                ...(settingsIntegrations || {}),
                calendlyStatus: { connected: false },
            });
            setTimeout(() => setCalendlySuccess(''), 3000);
        } else {
            setCalendlyError('Failed to disconnect');
        }
        setCalendlyDisconnecting(false);
    };

    const handleSyncCalendly = async () => {
        setCalendlySyncing(true);
        setCalendlyError('');
        const result = await syncCalendlyEvents();
        if (result.success) {
            setCalendlySuccess(`Synced ${result.synced || 0} events`);
            await loadCalendlyStatus();
            setTimeout(() => setCalendlySuccess(''), 3000);
        } else {
            setCalendlyError('Sync failed. Please try again.');
        }
        setCalendlySyncing(false);
    };

    const handleSaveTwilio = async () => {
        const userId = auth.currentUser?.uid;
        if (!userId) return;

        setTwilioSaving(true);
        try {
            const hasTwilio = twilioSid.trim() && twilioPhone.trim();
            const hasSendGrid = sendgridApiKey.trim() && !sendgridApiKey.startsWith('••') ? true : (emailConnected && sendgridApiKey.startsWith('••'));
            const hasFromEmail = sendgridFromEmail.trim();

            if (hasTwilio || (hasSendGrid && hasFromEmail)) {
                const payload = {
                    connectedAt: new Date().toISOString(),
                };

                // Twilio SMS fields
                if (twilioSid.trim()) payload.accountSid = twilioSid.trim();
                if (twilioPhone.trim()) payload.phoneNumber = twilioPhone.trim();
                if (twilioToken && !twilioToken.startsWith('••')) {
                    payload.authToken = twilioToken.trim();
                }

                // SendGrid Email fields
                if (sendgridApiKey && !sendgridApiKey.startsWith('••')) {
                    payload.sendgridApiKey = sendgridApiKey.trim();
                }
                if (sendgridFromEmail.trim()) {
                    payload.sendgridFromEmail = sendgridFromEmail.trim();
                }

                // Inbound domain (optional)
                const domainVal = inboundDomain.trim() || null;
                payload.inboundDomain = domainVal;
                payload.emailInboundEnabled = !!domainVal;

                await setDoc(doc(db, 'users', userId, 'integrations', 'twilio'), payload, { merge: true });
                setTwilioConnected(!!(twilioSid.trim() && twilioPhone.trim()));
                setEmailConnected(!!(sendgridFromEmail.trim() && (sendgridApiKey.startsWith('••') || sendgridApiKey.trim())));
                setEmailInboundEnabled(!!domainVal);
                // Write-through: update Zustand integration cache
                setSettingsIntegrations({
                    ...(settingsIntegrations || {}),
                    twilioConnected: !!(twilioSid.trim() && twilioPhone.trim()),
                    emailConnected: !!(sendgridFromEmail.trim() && (sendgridApiKey.startsWith('••') || sendgridApiKey.trim())),
                    emailInboundEnabled: !!domainVal,
                });
                if (twilioToken && !twilioToken.startsWith('••')) {
                    setTwilioToken('••••••••');
                }
                if (sendgridApiKey && !sendgridApiKey.startsWith('••')) {
                    setSendgridApiKey('••••••••');
                }
            } else {
                await deleteDoc(doc(db, 'users', userId, 'integrations', 'twilio'));
                setTwilioConnected(false);
                setEmailConnected(false);
                setEmailInboundEnabled(false);
                setTwilioSid('');
                setTwilioToken('');
                setTwilioPhone('');
                setSendgridApiKey('');
                setSendgridFromEmail('');
                setInboundDomain('');
            }
        } catch (error) {
            console.error('Error saving integration:', error);
        } finally {
            setTwilioSaving(false);
        }
    };

    const handleDisconnectTwilio = async () => {
        const userId = auth.currentUser?.uid;
        if (!userId) return;

        setTwilioSaving(true);
        try {
            await deleteDoc(doc(db, 'users', userId, 'integrations', 'twilio'));
            setTwilioSid('');
            setTwilioToken('');
            setTwilioPhone('');
            setSendgridApiKey('');
            setSendgridFromEmail('');
            setInboundDomain('');
            setEmailInboundEnabled(false);
            setTwilioConnected(false);
            setEmailConnected(false);
            // Write-through: clear Zustand integration cache
            setSettingsIntegrations({
                ...(settingsIntegrations || {}),
                twilioConnected: false,
                emailConnected: false,
                emailInboundEnabled: false,
            });
        } catch (error) {
            console.error('Error disconnecting:', error);
        } finally {
            setTwilioSaving(false);
        }
    };

    const formatDate = (date) => {
        if (!date) return 'Never';
        return new Intl.DateTimeFormat('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: 'numeric', minute: '2-digit',
        }).format(date);
    };

    const copyToClipboard = async (value, key) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopiedField(key);
            setTimeout(() => setCopiedField(''), 1500);
        } catch (error) {
            console.error('Copy failed:', error);
        }
    };

    return (
        <div>
            <div className="mb-4 md:mb-6">
                <h2 className="text-lg font-bold text-notion-text">Integrations</h2>
                <p className="text-xs text-stone-500">{isMobile ? 'Connected services.' : 'Connect scheduling and communication services.'}</p>
            </div>

            <div className="space-y-4 md:space-y-6">
                {/* Calendly OAuth Integration */}
                <div className="bg-white border border-notion-border rounded-xl p-5 md:p-6 shadow-card">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center">
                                <Calendar className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-notion-text">Calendly</h3>
                                <p className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-stone-400`}>
                                    {isMobile ? 'Meeting scheduling' : 'Sync meetings and show upcoming schedule on Dashboard'}
                                </p>
                            </div>
                        </div>
                        {calendlyStatus.connected && (
                            <span className="flex items-center gap-1 text-[10px] text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded">
                                <CheckCircle2 className="w-3 h-3" /> Connected
                            </span>
                        )}
                    </div>

                    {/* Error / Success messages */}
                    {calendlyError && (
                        <div className="mb-3 flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                            {calendlyError}
                        </div>
                    )}
                    {calendlySuccess && (
                        <div className="mb-3 flex items-center gap-2 text-xs text-green-600 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                            <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
                            {calendlySuccess}
                        </div>
                    )}

                    {calendlyLoading ? (
                        <div className="flex items-center gap-2 py-4">
                            <Loader2 className="w-4 h-4 animate-spin text-stone-400" />
                            <span className="text-xs text-stone-400">Loading status...</span>
                        </div>
                    ) : calendlyStatus.connected ? (
                        /* Connected state */
                        <div className="space-y-3">
                            <div className="bg-notion-sidebar rounded-lg p-3 space-y-1.5">
                                <div className="flex items-center justify-between">
                                    <span className="text-[10px] text-stone-500 font-medium uppercase tracking-wide">Account</span>
                                </div>
                                <p className="text-sm text-notion-text font-medium">{calendlyStatus.email}</p>
                                {calendlyStatus.name && (
                                    <p className="text-xs text-stone-500">{calendlyStatus.name}</p>
                                )}
                                <p className="text-[10px] text-stone-400">
                                    Last synced: {formatDate(calendlyStatus.lastSync)}
                                </p>
                            </div>

                            <div className={`flex gap-2 ${isMobile ? 'flex-col' : ''}`}>
                                <button
                                    onClick={handleSyncCalendly}
                                    disabled={calendlySyncing}
                                    className={`${isMobile ? 'w-full' : ''} bg-stone-800 text-white px-4 ${isMobile ? 'py-2.5' : 'py-2'} rounded ${isMobile ? 'text-sm' : 'text-xs'} font-medium hover:bg-stone-700 disabled:opacity-50 flex items-center justify-center gap-1.5`}
                                >
                                    {calendlySyncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                                    Sync Now
                                </button>
                                <button
                                    onClick={handleDisconnectCalendly}
                                    disabled={calendlyDisconnecting}
                                    className={`${isMobile ? 'w-full' : ''} px-4 ${isMobile ? 'py-2.5' : 'py-2'} rounded ${isMobile ? 'text-sm' : 'text-xs'} font-medium border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 flex items-center justify-center gap-1.5`}
                                >
                                    {calendlyDisconnecting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unplug className="w-3 h-3" />}
                                    Disconnect
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* Not connected state */
                        <div className="space-y-3">
                            <p className="text-xs text-stone-500">
                                Connect your Calendly account to automatically sync meetings and display your upcoming schedule on the Dashboard.
                            </p>
                            <button
                                onClick={handleConnectCalendly}
                                disabled={calendlyConnecting}
                                className={`${isMobile ? 'w-full' : ''} bg-blue-600 text-white px-5 ${isMobile ? 'py-2.5' : 'py-2'} rounded ${isMobile ? 'text-sm' : 'text-xs'} font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2`}
                            >
                                {calendlyConnecting ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                    <ExternalLink className="w-3.5 h-3.5" />
                                )}
                                {calendlyConnecting ? 'Connecting...' : 'Connect Calendly'}
                            </button>
                        </div>
                    )}
                </div>

                {/* Twilio / SendGrid Integration */}
                <div className="bg-white border border-notion-border rounded-xl p-5 md:p-6 shadow-card">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-red-50 border border-red-200 rounded-lg flex items-center justify-center">
                                <Phone className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-notion-text">Twilio / SendGrid</h3>
                                <p className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-stone-400`}>
                                    {isMobile ? 'SMS & Email' : 'SMS and email communication with leads'}
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                            {twilioConnected && (
                                <span className="flex items-center gap-1 text-[10px] text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded">
                                    <CheckCircle2 className="w-3 h-3" /> SMS Enabled
                                </span>
                            )}
                            {emailConnected && (
                                <span className="flex items-center gap-1 text-[10px] text-blue-600 font-medium bg-blue-50 px-2 py-0.5 rounded">
                                    <Mail className="w-3 h-3" /> Email Enabled
                                </span>
                            )}
                            {emailInboundEnabled && (
                                <span className="flex items-center gap-1 text-[10px] text-purple-600 font-medium bg-purple-50 px-2 py-0.5 rounded">
                                    <CheckCircle2 className="w-3 h-3" /> Inbound Enabled
                                </span>
                            )}
                            {emailConnected && !emailInboundEnabled && (
                                <span className="flex items-center gap-1 text-[10px] text-stone-500 font-medium bg-stone-100 px-2 py-0.5 rounded">
                                    Outbound Only
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="space-y-4">
                        {/* SMS Section */}
                        <div>
                            <p className="text-[10px] font-semibold text-stone-500 uppercase tracking-wide mb-2">SMS (Twilio)</p>
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-[10px] font-medium text-stone-500 mb-1">Account SID</label>
                                    <input
                                        type="text"
                                        value={twilioSid}
                                        onChange={(e) => setTwilioSid(e.target.value)}
                                        placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                                        className={`w-full bg-notion-sidebar border-transparent rounded px-3 ${isMobile ? 'py-2.5 text-sm' : 'py-2 text-xs'} outline-none focus:border-stone-300 border transition font-mono`}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-medium text-stone-500 mb-1">Auth Token</label>
                                    <input
                                        type="password"
                                        value={twilioToken}
                                        onChange={(e) => setTwilioToken(e.target.value)}
                                        placeholder="Your auth token"
                                        className={`w-full bg-notion-sidebar border-transparent rounded px-3 ${isMobile ? 'py-2.5 text-sm' : 'py-2 text-xs'} outline-none focus:border-stone-300 border transition font-mono`}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-medium text-stone-500 mb-1">Phone Number</label>
                                    <input
                                        type="tel"
                                        value={twilioPhone}
                                        onChange={(e) => setTwilioPhone(e.target.value)}
                                        placeholder="+1 (555) 000-0000"
                                        className={`w-full bg-notion-sidebar border-transparent rounded px-3 ${isMobile ? 'py-2.5 text-sm' : 'py-2 text-xs'} outline-none focus:border-stone-300 border transition`}
                                    />
                                </div>

                                {twilioConnected && (
                                    <div className="bg-stone-50 border border-notion-border rounded-lg p-3 space-y-2">
                                        <label className="block text-[10px] font-medium text-stone-600">Webhook URL</label>
                                        <div className={`flex gap-2 ${isMobile ? 'flex-col' : ''}`}>
                                            <input
                                                type="text"
                                                readOnly
                                                value={twilioWebhookUrl}
                                                className="flex-1 bg-white border border-notion-border rounded px-3 py-2 text-xs font-mono text-stone-700"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => copyToClipboard(twilioWebhookUrl, 'twilio')}
                                                className={`${isMobile ? 'w-full' : ''} inline-flex items-center justify-center gap-1.5 border border-notion-border rounded px-3 py-2 text-xs font-medium text-stone-700 hover:bg-stone-100`}
                                            >
                                                {copiedField === 'twilio' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                                {copiedField === 'twilio' ? 'Copied' : 'Copy'}
                                            </button>
                                        </div>
                                        <p className="text-[10px] text-stone-500">
                                            Paste this URL in your Twilio settings under "A message comes in".
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Divider */}
                        <div className="border-t border-notion-border" />

                        {/* Email Section */}
                        <div>
                            <p className="text-[10px] font-semibold text-stone-500 uppercase tracking-wide mb-2">Email (SendGrid)</p>
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-[10px] font-medium text-stone-500 mb-1">SendGrid API Key</label>
                                    <input
                                        type="password"
                                        value={sendgridApiKey}
                                        onChange={(e) => setSendgridApiKey(e.target.value)}
                                        placeholder="SG.xxxxxxxxxxxxxxxxxxxx"
                                        className={`w-full bg-notion-sidebar border-transparent rounded px-3 ${isMobile ? 'py-2.5 text-sm' : 'py-2 text-xs'} outline-none focus:border-stone-300 border transition font-mono`}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-medium text-stone-500 mb-1">From Email</label>
                                    <input
                                        type="email"
                                        value={sendgridFromEmail}
                                        onChange={(e) => setSendgridFromEmail(e.target.value)}
                                        placeholder="you@yourdomain.com"
                                        className={`w-full bg-notion-sidebar border-transparent rounded px-3 ${isMobile ? 'py-2.5 text-sm' : 'py-2 text-xs'} outline-none focus:border-stone-300 border transition`}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-medium text-stone-500 mb-1">Inbound Domain <span className="text-stone-400 font-normal">(optional)</span></label>
                                    <input
                                        type="text"
                                        value={inboundDomain}
                                        onChange={(e) => setInboundDomain(e.target.value)}
                                        placeholder="reply.yourdomain.com"
                                        className={`w-full bg-notion-sidebar border-transparent rounded px-3 ${isMobile ? 'py-2.5 text-sm' : 'py-2 text-xs'} outline-none focus:border-stone-300 border transition font-mono`}
                                    />
                                    <p className="text-[10px] text-stone-400 mt-1">
                                        Set this if you configured SendGrid Inbound Parse for receiving replies. Leave empty for outbound-only email.
                                    </p>
                                </div>

                                {emailConnected && (
                                    <div className="bg-stone-50 border border-notion-border rounded-lg p-3 space-y-2">
                                        <label className="block text-[10px] font-medium text-stone-600">Inbound Parse Webhook URL</label>
                                        <div className={`flex gap-2 ${isMobile ? 'flex-col' : ''}`}>
                                            <input
                                                type="text"
                                                readOnly
                                                value={sendgridWebhookUrl}
                                                className="flex-1 bg-white border border-notion-border rounded px-3 py-2 text-xs font-mono text-stone-700"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => copyToClipboard(sendgridWebhookUrl, 'sendgrid')}
                                                className={`${isMobile ? 'w-full' : ''} inline-flex items-center justify-center gap-1.5 border border-notion-border rounded px-3 py-2 text-xs font-medium text-stone-700 hover:bg-stone-100`}
                                            >
                                                {copiedField === 'sendgrid' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                                {copiedField === 'sendgrid' ? 'Copied' : 'Copy'}
                                            </button>
                                        </div>
                                        <p className="text-[10px] text-stone-500">
                                            Paste this URL in SendGrid Inbound Parse settings.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-start gap-2 text-[10px] text-stone-400">
                            <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            <span>Credentials are stored in your account and used only for communicating with your leads.</span>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={handleSaveTwilio}
                                disabled={twilioSaving}
                                className={`${isMobile ? 'flex-1' : ''} bg-stone-800 text-white px-4 ${isMobile ? 'py-2.5' : 'py-2'} rounded ${isMobile ? 'text-sm' : 'text-xs'} font-medium hover:bg-stone-700 disabled:opacity-50 flex items-center justify-center gap-1.5`}
                            >
                                {twilioSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                                {(twilioConnected || emailConnected) ? 'Update' : 'Connect'}
                            </button>
                            {(twilioConnected || emailConnected) && (
                                <button
                                    onClick={handleDisconnectTwilio}
                                    disabled={twilioSaving}
                                    className={`px-4 ${isMobile ? 'py-2.5' : 'py-2'} rounded ${isMobile ? 'text-sm' : 'text-xs'} font-medium border border-notion-border text-stone-600 hover:bg-stone-50 disabled:opacity-50`}
                                >
                                    Disconnect
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IntegrationsSection;
